<template>
  <div id="photos">
    <v-overlay :value="loadingPatientData" absolute>
      <v-progress-circular indeterminate size="64" />
    </v-overlay>
    <div v-if="!loadingPatientData">
      <v-row class="mb-4">
        <v-col cols="12" md="6" lg="4">
          <v-card class="patient-info-card">
            <h1>{{ $t("patient_information") }}</h1>
            <div class="mb-2">
              <strong>{{ `${this.$t("initials")}: ` }}</strong>
              {{ `${this.patient.initials}` }}
            </div>
            <div class="mb-2">
              <strong>{{ `${this.$t("gender")}: ` }}</strong>
              {{ `${this.$t(this.patient.gender)}` }}
            </div>
            <div class="mb-2">
              <strong>{{ `${this.$t("age")}: ` }}</strong>
              {{ `${calculateAge(this.patient.birthdate)}` }}
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-card v-if="bodyParts.headNeck && bodyParts.headNeck.length">
        <div
          class="d-flex justify-space-between align-center cursor-pointer"
          @click="headNeckCard = !headNeckCard"
        >
          <h2>{{ $t("head_neck") }}</h2>
          <v-btn icon>
            <v-icon x-large>{{
              headNeckCard ? "mdi-chevron-up" : "mdi-chevron-down"
            }}</v-icon>
          </v-btn>
        </div>

        <div v-show="headNeckCard">
          <photo-gallery :content="bodyParts.headNeck" />

          <div class="d-flex justify-space-between flex-wrap"></div>
        </div>
      </v-card>

      <v-card
        class="mt-8"
        v-if="bodyParts.upperLimbs && bodyParts.upperLimbs.length"
      >
        <div
          class="d-flex justify-space-between align-center cursor-pointer"
          @click="upperLimbsCard = !upperLimbsCard"
        >
          <h2>{{ $t("upper_limbs") }}</h2>
          <v-btn icon>
            <v-icon x-large>{{
              upperLimbsCard ? "mdi-chevron-up" : "mdi-chevron-down"
            }}</v-icon>
          </v-btn>
        </div>

        <div v-show="upperLimbsCard">
          <photo-gallery :content="bodyParts.upperLimbs" />

          <div class="d-flex flex-wrap"></div>
        </div>
      </v-card>

      <v-card class="mt-8" v-if="bodyParts.torso && bodyParts.torso.length">
        <div
          class="d-flex justify-space-between align-center cursor-pointer"
          @click="torsoCard = !torsoCard"
        >
          <h2>{{ $t("torso") }}</h2>
          <v-btn icon>
            <v-icon x-large>{{
              torsoCard ? "mdi-chevron-up" : "mdi-chevron-down"
            }}</v-icon>
          </v-btn>
        </div>

        <div v-show="torsoCard">
          <photo-gallery :content="bodyParts.torso" />

          <div class="d-flex flex-wrap"></div>
        </div>
      </v-card>

      <v-card
        class="mt-8"
        v-if="bodyParts.lowerLimbs && bodyParts.lowerLimbs.length"
      >
        <div
          class="d-flex justify-space-between align-center cursor-pointer"
          @click="lowerLimbsCard = !lowerLimbsCard"
        >
          <h2>{{ $t("lower_limbs") }}</h2>
          <v-btn icon>
            <v-icon x-large>{{
              lowerLimbsCard ? "mdi-chevron-up" : "mdi-chevron-down"
            }}</v-icon>
          </v-btn>
        </div>
        <div v-show="lowerLimbsCard">
          <photo-gallery :content="bodyParts.lowerLimbs" />

          <div class="d-flex flex-wrap"></div>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import moment from "moment";

import tokenMixin from "@/mixins/tokenMixin.js";
import PhotoGallery from "@/components/PhotoGallery.vue";

export default {
  name: "Photos",
  components: {
    PhotoGallery,
  },
  mixins: [tokenMixin],
  data() {
    return {
      patientForms: [],
      patient: null,
      headNeckCard: true,
      lowerLimbsCard: true,
      torsoCard: true,
      upperLimbsCard: true,
      loadingPatientData: true,
      bodyParts: {},
    };
  },
  computed: {
    patientId() {
      return this.$route.params.id;
    },
  },
  mounted() {
    this.fetchPatientDetail();
  },
  methods: {
    calculateVisitWeek(currentVisitDate) {
      const firstVisitDate = this.patientForms[this.patientForms.length - 1]
        .evaluated_on;
      const weekDiffrence = moment(currentVisitDate).diff(
        moment(firstVisitDate),
        "week"
      );
      return weekDiffrence;
    },
    async fetchPatientDetail() {
      await this.$_checkTokenExpiration();

      ({
        body: { forms: this.patientForms, patient: this.patient },
      } = await this.$http.get(`patients/${this.patientId}`));
      if (this.patientForms.length > 0) {
        this.patientForms = this.patientForms.slice().reverse();
        this.patientForms.forEach((form) =>
          form.body_parts.forEach((part) => {
            const key = part.type.replace(/_([a-z])/g, (g) =>
              g[1].toUpperCase()
            );
            if (part.images && part.images.length) {
              if (!this.bodyParts[key]) {
                this.bodyParts[key] = [];
              }
              this.bodyParts[key].push({
                ...part,
                date: form.evaluated_on,
                week: this.calculateVisitWeek(form.evaluated_on),
              });
            }
          })
        );
      }
      this.loadingPatientData = false;
    },
    calculateAge(birthDate) {
      const momentBirthDate = moment(birthDate);
      return moment.duration(moment().diff(momentBirthDate)).years();
    },
  },
};
</script>

<style lang="scss" scoped>
#photos {
  padding: 2rem 5rem;

  .v-card {
    box-shadow: 0px 0px 18px 0px rgba(209, 209, 209, 1);
    padding: 1rem;
  }

  .small-card-wrapper {
    width: 20%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-bottom: 2rem;
  }

  .patient-info-card {
    height: 100%;
    padding: 1rem;
    font-size: 1.5rem;

    h1 {
      margin-bottom: 1rem;
      font-size: 1.7rem;
    }

    strong {
      color: #324c8d;
    }
  }

  .partner-banner {
    height: 100%;
  }

  .easi-chart-wrapper {
    width: 78%;
    box-shadow: 0px 0px 18px 0px rgba(209, 209, 209, 1);
    padding: 1rem;
    min-height: 420px;
  }

  .body-part-title-wrapper {
    padding: 0 2rem;
    width: 39%;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
      max-width: 500px;
      width: 100%;
    }
  }

  .range-chart {
    width: 60%;
  }

  .small-chart {
    width: 24%;
    margin-top: 2rem;
  }

  .cursor-pointer {
    cursor: pointer;
  }

  @media screen and (max-width: 1100px) {
    padding: 2rem;

    .body-part-title-wrapper {
      display: none;
    }

    .range-chart {
      width: 100%;
    }
  }

  @media screen and (max-width: 959px) {
    padding: 1rem;

    .small-card-wrapper,
    .easi-chart-wrapper,
    .small-chart {
      width: 100%;
    }

    .patient-info-card {
      margin-bottom: 2rem;
    }
  }
}
</style>
